import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, RefreshCw, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import useSubscriptionStatus from '../hooks/useSubscriptionStatus';

const SuccessPage: React.FC = () => {
  const { user } = useAuth();
  const { supabase } = useSupabase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productName = searchParams.get('product');
  const durationMonths = searchParams.get('duration') || '1';
  const isMock = searchParams.get('mock') === 'true';

  const [error, setError] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  const {
    isSubscribed,
    subscription,
    loading: subscriptionLoading,
    error: subscriptionError,
    refresh,
  } = useSubscriptionStatus();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    let isMounted = true;
    let pollingInterval: NodeJS.Timeout;
    let redirectTimer: NodeJS.Timeout;

    const checkSubscriptionStatus = async () => {
      try {
        await refresh();

        console.log('[checkSubscriptionStatus] isSubscribed:', isSubscribed);
        console.log('[checkSubscriptionStatus] subscriptionLoading:', subscriptionLoading);

        if (isMounted && isSubscribed) {
          clearInterval(pollingInterval);

          let count = 5;
          redirectTimer = setInterval(() => {
            count--;
            if (isMounted) {
              setRedirectCountdown(count);
              if (count <= 0) {
                clearInterval(redirectTimer);
                navigate('/');
              }
            }
          }, 1000);
        }
      } catch (error: any) {
        console.error('Erro ao verificar status da assinatura:', error);
      }
    };

    const processPayment = async () => {
      try {
        await supabase.from('notificacoes').insert({
          usuario_id: user.id,
          titulo: 'Assinatura confirmada!',
          mensagem: `Bem-vindo ao Presida.Club! ${isMock ? '(MODO TESTE) ' : ''}Sua assinatura${productName ? ` do plano ${productName}` : ''} foi ativada com sucesso por ${durationMonths} ${parseInt(durationMonths) === 1 ? 'mês' : 'meses'}.`,
        });

        if (isMock) {
          const expirationDate = new Date();
          expirationDate.setMonth(expirationDate.getMonth() + parseInt(durationMonths));

          await supabase
            .from('profiles')
            .update({
              plano_atual: productName || 'Premium',
              plano_expira_em: expirationDate.toISOString(),
              trial_ends_at: null,
            })
            .eq('id', user.id);

          let count = 5;
          redirectTimer = setInterval(() => {
            count--;
            if (isMounted) {
              setRedirectCountdown(count);
              if (count <= 0) {
                clearInterval(redirectTimer);
                navigate('/');
              }
            }
          }, 1000);

          return;
        }

        pollingInterval = setInterval(checkSubscriptionStatus, 3000);

        await checkSubscriptionStatus();

        setTimeout(() => {
          if (isMounted) {
            clearInterval(pollingInterval);
            if (!isSubscribed) {
              setError('Seu pagamento foi processado, mas estamos tendo problemas para confirmar sua assinatura. Por favor, aguarde alguns minutos e verifique seu status na página de perfil.');
            }
          }
        }, 30000);
      } catch (error) {
        console.error('Error processing payment:', error);
        if (isMounted) {
          setError('Ocorreu um erro ao processar o pagamento. Por favor, entre em contato com o suporte.');
        }
      }
    };

    processPayment();

    return () => {
      isMounted = false;
      clearInterval(pollingInterval);
      clearInterval(redirectTimer);
    };
  }, [user, supabase, navigate, productName, durationMonths, isMock, refresh, isSubscribed]);

  const handleManualRefresh = async () => {
    try {
      await refresh();
      if (isSubscribed) {
        setTimeout(() => navigate('/'), 1000);
      }
    } catch (err) {
      console.error('Error during manual refresh:', err);
    }
  };

  const getNextBillingDate = () => {
    if (!subscription?.current_period_end) return 'Data não disponível';

    try {
      const date = new Date(subscription.current_period_end);
      return date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (e) {
      return 'Data não disponível';
    }
  };

  const overallLoading = subscriptionLoading;

  if (overallLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Processando pagamento...</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Estamos confirmando seu pagamento e ativando sua assinatura. Isso pode levar alguns instantes.
          </p>
        </div>
      </div>
    );
  }

  if (!subscriptionLoading && !isSubscribed && !isMock) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Erro no processamento</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
            {error ?? 'Seu pagamento foi recebido, mas não conseguimos confirmar sua assinatura.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing" className="btn-outline">
              Voltar para planos
            </Link>
            <button onClick={handleManualRefresh} className="btn-primary flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Verificar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-green-100 dark:bg-green-900/20">
          <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-3xl font-bold mb-4">Pagamento Confirmado!</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
          {isMock ? (
            <>
              <span className="text-yellow-500 font-bold">[MODO TESTE] </span>
              Esta é uma simulação de pagamento para fins de desenvolvimento.
            </>
          ) : (
            <>
              Obrigado por assinar o Presida.Club! Seu pagamento foi processado com sucesso e sua assinatura está ativa.
            </>
          )}
          {productName && (
            <span className="block mt-2">
              Plano: <strong>{productName}</strong> - Duração: <strong>{durationMonths} {parseInt(durationMonths) === 1 ? 'mês' : 'meses'}</strong>
            </span>
          )}
          {isSubscribed && subscription && (
            <span className="block mt-2 text-green-600 dark:text-green-400">
              Status da assinatura: <strong>Ativa</strong>
              <br />
              Próxima cobrança: <strong>{getNextBillingDate()}</strong>
            </span>
          )}
          {isSubscribed && !subscription && (
            <span className="block mt-2 text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              Redirecionando para o dashboard em {redirectCountdown} segundos...
            </span>
          )}
          {subscriptionError && (
            <span className="block mt-2 text-red-500 text-sm">
              Erro ao verificar status: {subscriptionError}
            </span>
          )}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/" className="btn-primary">
            Ir para o Dashboard
          </Link>
          <Link to="/pricing" className="btn-outline">
            Ver planos
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
